import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";

/* ===== HEADER ITEMS ===== */
const headerItems = [
  { name: "Home", href: "/" },
  { name: "About Us", href: "/about" },
  { name: "Team", href: "/team" },
  { name: "Subunits", href: "/subunits" },
  { name: "Awards", href: "/awards" },
  { name: "Events", href: "/events" },
  { name: "Contact Us", href: "/contact" },
];

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.8,
        ease: [0.43, 0.13, 0.23, 0.96],
        staggerChildren: 0.07,
      },
    },
  };

  const mobileItemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] },
    },
  };

  // Scroll to section or top
  const scrollToSection = (id) => {
    if (id) {
      const section = document.getElementById(id);
      if (section) section.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Handle menu clicks
  const handleClick = (item) => {
    setIsMobileMenuOpen(false);

    if (item.scrollId) {
      if (location.pathname !== "/") {
        navigate("/", { replace: false });
        setTimeout(() => scrollToSection(item.scrollId), 100); // wait for home render
      } else {
        scrollToSection(item.scrollId);
      }
    } else if (item.href === "/" && location.pathname === "/") {
      // Home clicked while on home page
      scrollToSection(null);
    } else if (item.href) {
      navigate(item.href);
    }
  };

  // Handle logo click (always scroll to top of home)
  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      navigate("/", { replace: false });
      setTimeout(() => scrollToSection(null), 100);
    } else {
      scrollToSection(null);
    }
  };

  return (
    <>
      {/* ===== TOP IEEE BAR ===== */}
      <div className="w-full bg-[#006699] text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-2 flex flex-wrap justify-center md:justify-end gap-4 md:gap-8">
          <a
            href="https://www.ieee.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-200 transition-colors"
          >
            IEEE.org
          </a>
          <a
            href="https://ieeexplore.ieee.org/Xplore/home.jsp"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-200 transition-colors"
          >
            IEEE Xplore
          </a>
          <a
            href="https://ieee-collabratec.ieee.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-200 transition-colors"
          >
            Collabratec
          </a>
          <a
            href="https://www.ieee.org/profile/public/createwebaccount/showCreateAccount.html"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:text-blue-200 transition-colors"
          >
            Join IEEE
          </a>
        </div>
      </div>

      {/* ===== HEADER ===== */}
      <header className="h-[90px] flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-16 xl:px-20 sticky top-0 z-40">
        <div className="absolute inset-0 bg-gradient-to-r from-[#006699] via-[#006699] to-transparent lg:via-transparent opacity-5"></div>
        <div className="absolute inset-0 bg-white/95 backdrop-blur-md border-b border-blue-100/70 shadow-glow"></div>

        <div className="relative z-10 flex items-center justify-between w-full">
          {/* Logo animation ONLY */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex items-center gap-3 sm:gap-4 cursor-pointer"
            onClick={handleLogoClick}
          >
            <img
              src="/asset/logos/logosb.png"
              alt="IEEE ISIMS SB Logo"
              className="h-12 w-auto object-contain p-1"
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(28%) sepia(29%) saturate(1337%) hue-rotate(158deg)",
              }}
            />
          </motion.div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            <nav className="flex items-center gap-8 xl:gap-10">
              {headerItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.05, duration: 0.6 }}
                >
                  <button
                    onClick={() => handleClick(item)}
                    className="text-[#00334d] hover:text-[#006699] transition-all duration-300 font-light text-xs uppercase tracking-widest relative group"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-[#006699] transition-all duration-300 group-hover:w-full"></span>
                  </button>
                </motion.div>
              ))}
            </nav>

            {/* Button animation ONLY */}
            <Link
            to="/join"
            className="hover:text-[#006699] transition-colors"
          >
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="bg-[#006699] hover:bg-white hover:text-[#006699] text-white px-6 py-2.5 rounded-none border border-[#006699] font-medium text-xs tracking-widest uppercase transition-all duration-300 shadow-md cursor-pointer"
            >
              
              Join IEEE ISIMS SB
            </motion.button>
        
          </Link>
            
            
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#006699] hover:bg-blue-50 p-2 rounded-none cursor-pointer"
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </header>

      {/* ===== MOBILE MENU ===== */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="lg:hidden fixed top-[90px] left-0 right-0 z-40 bg-white shadow-xl border-t border-[#006699]/10"
          >
            <div className="px-6 py-8 space-y-6">
              {headerItems.map((item) => (
                <motion.div key={item.name} variants={mobileItemVariants}>
                  <button
                    onClick={() => handleClick(item)}
                    className="block text-[#00334d] hover:text-[#006699] font-light text-xs uppercase tracking-widest cursor-pointer w-full text-left"
                  >
                    {item.name}
                  </button>
                </motion.div>
              ))}

              {/* Thin separator line */}
              <div className="w-12 h-[1px] bg-[#006699]/20 mt-4 mb-4"></div>

              <Link
            to="/join"
            className="block w-full"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {/* Join IEEE button */}
            <motion.button
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full bg-[#006699] hover:bg-[#004d73] text-white px-6 py-3 rounded-none border border-[#006699] font-medium text-xs tracking-widest uppercase transition-all duration-300 shadow-md cursor-pointer"
              >
                Join IEEE ISIMS SB
              </motion.button>
            
        
          </Link>

              
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Outlet for React Router */}
      <Outlet />
    </>
  );
}
